// Sections 8 and 11: skill factor (0.6 + 0.1 x skill), a master's output while teaching
// apprentices (0.85x), skilled-work happiness, the literacy bonus (+0.05 skill gain in jobs) and
// Section 6's safety row (children's learning rate halves after 2+ seasons short of safety).
import { defineContributor } from '../../core/registry';
import { mod } from '../../core/modifiers';
import { activeApprentices, isChild, skillOf } from '../../core/queries';
import type { Person, ReadContext } from '../../core/types';

/** Section 6: short of safety this season and the seasons before it (config.skills). */
function unsafeForLearning(ctx: ReadContext, p: Person): boolean {
  const s = ctx.state;
  const n = ctx.cfg.skills.safetyShortLearningSeasons;
  const reports = s.currentReport === null ? [...s.history] : [...s.history, s.currentReport];
  const recent = reports.slice(Math.max(0, reports.length - n));
  const short = ctx.cfg.needs.shortThreshold;
  return recent.length >= n && recent.every((r) => (r.needs[p.id]?.safety ?? 1) < short);
}

export const skillsContributor = defineContributor({
  slot: 'skills',
  id: 'skills:core',
  targets: ['output', 'happinessTarget', 'skillGain'],
  modifiers(ctx, target) {
    if (!('personId' in target)) return [];
    const p = ctx.state.people[target.personId];
    if (p === undefined) return [];
    const cfg = ctx.cfg;
    switch (target.kind) {
      case 'output': {
        const k = skillOf(p, target.jobId);
        const out = [
          mod.mul(
            `skill:${target.jobId}`,
            cfg.work.skillBase + cfg.work.skillPerLevel * k,
            `skill ${k.toFixed(1)}`,
          ),
        ];
        const apprentices = activeApprentices(cfg, ctx.state)[p.id]?.length ?? 0;
        if (apprentices > 0 && target.jobId === p.job) {
          out.push(
            mod.mul(
              'skill:master',
              cfg.skills.masterOutputMul,
              `teaching ${apprentices} apprentice${apprentices === 1 ? '' : 's'}`,
            ),
          );
        }
        return out;
      }
      case 'happinessTarget':
        return p.job !== 'rest' && skillOf(p, p.job) >= cfg.skills.skilledAt
          ? [mod.add(`skill:${p.job}`, cfg.happiness.skilledWork, 'skilled at their work')]
          : [];
      case 'skillGain': {
        const out = [];
        if (p.status.literate && target.via !== 'school')
          out.push(mod.add('status:literate', cfg.skills.literateGainBonus, 'literate'));
        if (isChild(cfg, p) && unsafeForLearning(ctx, p))
          out.push(mod.mul('need:safety', cfg.needs.safetyChildLearningMul, 'unsafe to learn'));
        return out;
      }
      default:
        return [];
    }
  },
});
